import { useMemo } from "react";

import isNil from "lodash/isNil";

import {
  InTreatmentUniversalStatusCodes,
  PatientTreatmentPhases,
  PostTreatmentUniversalStatusCodes,
  PreTreatmentUniversalStatusCodes,
} from "@/constants/User";

interface UsePatientStatus {
  hasLinkedPatientInTreatment: boolean;
  isCurrentOrSupportLinkedUserInTreatment: boolean;
  isCurrentUserInTreatment: boolean;
}

const PreTreatmentCodes = new Set<UniversalStatusCode>(
  PreTreatmentUniversalStatusCodes,
);

const InTreatmentCodes = new Set<UniversalStatusCode>(
  InTreatmentUniversalStatusCodes,
);

const PostTreatmentCodes = new Set<UniversalStatusCode>(
  PostTreatmentUniversalStatusCodes,
);

const getUserTreatmentPhase = (
  statusCode?: UniversalStatusCode,
): TreatmentPhase | undefined => {
  if (isNil(statusCode)) {
    return undefined;
  }
  if (isUserPreTreatment(statusCode)) {
    return PatientTreatmentPhases.PreTreatment;
  }
  if (isUserInTreatment(statusCode)) {
    return PatientTreatmentPhases.InTreatment;
  }
  if (isUserPostTreatment(statusCode)) {
    return PatientTreatmentPhases.PostTreatment;
  }
  return PatientTreatmentPhases.OutOfTreatment;
};

const isUserPostTreatment = (statusCode: UniversalStatusCode): boolean => {
  return PostTreatmentCodes.has(statusCode);
};

const isUserPreTreatment = (statusCode: UniversalStatusCode): boolean => {
  return PreTreatmentCodes.has(statusCode);
};

const isUserInTreatment = (statusCode: UniversalStatusCode): boolean => {
  return InTreatmentCodes.has(statusCode);
};

//  This hook uses the current value of the `treatmentEpisodesAndUniversalStatus` feature flag
//  and a UserProfile supplied by the consumer to derive the patient's status.

//  The legacy way uses the admin episode status, whereas the new way uses the universal status code,
//  both provided via the UserProfile.

//  When the `treatmentEpisodesAndUniversalStatus` FF is no longer required, this hook may be converted
//  to a regular function and the logic simplified.

const usePatientStatus = (profile?: FullProfileResponse): UsePatientStatus => {
  return useMemo<UsePatientStatus>(() => {
    const linkedPatientsInTreatment = (profile?.linkedPatients ?? []).filter(
      (p: LinkedPatientProfile) =>
        !isNil(p.universalStatusCode) &&
        isUserInTreatment(p.universalStatusCode),
    );

    const isCurrentUserInTreatment = isNil(profile?.universalStatusCode)
      ? false
      : isUserInTreatment(profile.universalStatusCode);

    const isCurrentOrSupportLinkedUserInTreatment =
      isCurrentUserInTreatment || linkedPatientsInTreatment.length > 0;

    return {
      hasLinkedPatientInTreatment: linkedPatientsInTreatment.length > 0,
      isCurrentOrSupportLinkedUserInTreatment,
      isCurrentUserInTreatment,
    };
  }, [profile]);
};

export {
  getUserTreatmentPhase,
  isUserInTreatment,
  isUserPostTreatment,
  isUserPreTreatment,
};
export default usePatientStatus;
